import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import IdentificationTypeDialog from './IdentificationTypeDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface IdentificationType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

const IdentificationTypesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedIdentificationType, setSelectedIdentificationType] = useState<IdentificationType | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Identification Type ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/identification-types/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Identification type deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete identification type');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete identification type. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Identification Type ----------
  const handleEditClick = (identificationType: IdentificationType) => {
    setSelectedIdentificationType(identificationType);
    setEditDialogOpen(true);
  };

  // ---------- Add Identification Type ----------
  const handleAddIdentificationType = () => {
    setSelectedIdentificationType(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (identificationType: IdentificationType) => {
    setToggleLoading(identificationType.id);

    try {
      const endpoint = identificationType.active
        ? `api/identification-types/deactivate?id=${identificationType.id}`
        : `api/identification-types/activate?id=${identificationType.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Identification type ${identificationType.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update identification type status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update identification type status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedIdentificationType ? 'Identification type updated successfully!' : 'Identification type created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Type Name',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || (
            <span style={{ fontStyle: 'italic' }}>
              No description provided
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Identification Type',
      icon: <AddIcon />,
      onClick: handleAddIdentificationType,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Identification Types Function ----------
  const fetchIdentificationTypes = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchIdentificationTypes called with params:', params);
      try {
        const response = await DataService.get('api/identification-types/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawIdentificationTypes: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawIdentificationTypes = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawIdentificationTypes = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const identificationTypes: IdentificationType[] = rawIdentificationTypes.map(identificationType => ({
            id: identificationType.id,
            name: identificationType.name,
            description: identificationType.description,
            active: identificationType.active
          }));

          console.log('Raw identification types from API:', rawIdentificationTypes);
          console.log('Mapped identification types:', identificationTypes);

          // Filter by search term if provided
          let filteredIdentificationTypes = identificationTypes;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredIdentificationTypes = identificationTypes.filter(
              identificationType =>
                identificationType.name.toLowerCase().includes(searchLower) ||
                (identificationType.description && identificationType.description.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredIdentificationTypes.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof IdentificationType];
              let bValue: any = b[sortModel.field as keyof IdentificationType];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedIdentificationTypes = filteredIdentificationTypes.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredIdentificationTypes.length / params.pageSize);

          const result = {
            items: paginatedIdentificationTypes,
            totalCount: filteredIdentificationTypes.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch identification types: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching identification types:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load identification types. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Identification Types Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Identification Types"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchIdentificationTypes}
        actions={actions}
        emptyMessage="No identification types found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Identification Type"
        message="Are you sure you want to delete this identification type? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Identification Type Dialog */}
      <IdentificationTypeDialog
        open={editDialogOpen}
        identificationType={selectedIdentificationType}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default IdentificationTypesListing;